import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../../../../AxiosInstance";
import ProbeResult from "../../../../../components/network_quality/probe_result";
import { formatTimestamp } from "../../../../../utils/utils";
import { networksText } from "../../../../../utils/netquality";
import styles from "./network_section.module.css";

const WAIT_SECONDS = 75;
const SURVEY_MINUTES = 120;

/**
 * Tarjeta "Calidad de red" del detalle: medición a pedido (resultado en vivo
 * por WebSocket), configuración del muestreo automático y últimas
 * mediciones del equipo. Los mensajes del WebSocket los recibe
 * NetworkSection y los pasa por props. `absentMedia`: lo que el equipo no
 * tiene (p. ej. sin WiFi), para que la ayuda nombre solo sus redes.
 */
export default function NetworkQualityCard({ pdui_id, liveSamples, configStatus, absentMedia }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [interval, setIntervalValue] = useState(120);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [waiting, setWaiting] = useState(0);
  const [latest, setLatest] = useState([]);
  const requestedAt = useRef(null);

  const loadConfig = useCallback(() => {
    axiosInstance
      .get(`pdui/${pdui_id}/network-probe-config/`)
      .then((response) => {
        setConfig(response.data);
        setIntervalValue(response.data.interval_seconds);
      })
      .catch(() => {});
  }, [pdui_id]);

  const loadLatest = useCallback(() => {
    axiosInstance
      .get(`pdui/${pdui_id}/network-samples/?limit=9`)
      .then((response) => setLatest(response.data.samples || []))
      .catch(() => {});
  }, [pdui_id]);

  useEffect(() => {
    loadConfig();
    loadLatest();
  }, [loadConfig, loadLatest]);

  // Resultado de la medición pedida: muestras llegadas después del pedido.
  const results = (liveSamples || []).filter((s) => requestedAt.current && s._receivedAt >= requestedAt.current);
  const resultsByIface = Object.values(
    results.reduce((acc, s) => ({ ...acc, [s.interface]: s }), {}),
  );

  useEffect(() => {
    if (results.length > 0 && waiting > 0) {
      setMessage({ type: "success", text: t("netquality.results") });
      loadLatest();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results.length]);

  useEffect(() => {
    if (waiting <= 0) return undefined;
    const timer = setTimeout(() => {
      setWaiting((w) => {
        if (w <= 1) {
          if (results.length === 0) setMessage({ type: "warning", text: t("netquality.probeTimeout") });
          return 0;
        }
        return w - 1;
      });
    }, 1000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waiting]);

  useEffect(() => {
    if (!configStatus) return;
    if (configStatus.ok) {
      setMessage({ type: "success", text: t("netquality.deviceApplied") });
    } else {
      setMessage({ type: "error", text: t("netquality.deviceError", { error: configStatus.error || "" }) });
    }
  }, [configStatus, t]);

  const probe = async (kind) => {
    requestedAt.current = Date.now();
    setMessage({ type: "info", text: t("netquality.probeSent") });
    try {
      await axiosInstance.post(`pdui/${pdui_id}/network-probe/`, { kind });
      setWaiting(WAIT_SECONDS);
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.error || t("netquality.probeError") });
    }
  };

  const save = async (data) => {
    setSaving(true);
    try {
      const response = await axiosInstance.put(`pdui/${pdui_id}/network-probe-config/`, data);
      setConfig(response.data);
      setIntervalValue(response.data.interval_seconds);
      setMessage({
        type: response.data.delivered_to_device ? "success" : "warning",
        text: response.data.delivered_to_device ? t("netquality.saved") : t("netquality.savedNotDelivered"),
      });
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.error || t("netquality.saveError") });
    } finally {
      setSaving(false);
    }
  };

  const latestByIface = Object.values(
    [...latest].reverse().reduce((acc, s) => ({ ...acc, [s.interface]: s }), {}),
  ).sort((a, b) => (a.created_at < b.created_at ? 1 : -1));

  return (
    <div className={styles.qualityCard}>
      <div className={styles.qualityHeader}>
        <div>
          <div className={styles.qualityTitle}>{t("netquality.title")}</div>
          <div className={styles.qualityHelp}>
            {t("netquality.help", { networks: networksText(absentMedia, t("netquality.and")) })}
          </div>
        </div>
        <button type="button" className={styles.linkButton} onClick={() => navigate(`/network-map?pdui=${pdui_id}`)}>
          {t("netquality.viewMap")} →
        </button>
      </div>

      <div className={styles.qualityActions}>
        <button type="button" className={`${styles.button} ${styles.buttonOrange}`} onClick={() => probe("quick")} disabled={waiting > 0}>
          {waiting > 0 ? t("netquality.probing", { s: waiting }) : t("netquality.probeQuick")}
        </button>
        <button type="button" className={styles.button} onClick={() => probe("full")} disabled={waiting > 0}>
          {t("netquality.probeFull")}
        </button>
      </div>

      {message && (
        <div className={`${styles.alert} ${styles["alert" + message.type.charAt(0).toUpperCase() + message.type.slice(1)]}`}>
          {message.text}
        </div>
      )}

      {resultsByIface.length > 0 && (
        <div className={styles.qualityBlock}>
          <div className={styles.qualityLabel}>{t("netquality.results")}</div>
          <ProbeResult samples={resultsByIface} compact />
        </div>
      )}

      {config && (
        <div className={styles.qualityBlock}>
          <div className={styles.configRow}>
            <label className={styles.checkbox}>
              <input
                type="checkbox"
                checked={config.enabled}
                disabled={saving}
                onChange={(e) => save({ enabled: e.target.checked })}
              />
              <strong>{t("netquality.enabled")}</strong>
            </label>
            <span className={styles.configHelp}>{t("netquality.enabledHelp")}</span>
            <input
              type="number"
              className={styles.smallInput}
              min={config.min_seconds}
              max={config.max_seconds}
              value={interval}
              onChange={(e) => setIntervalValue(e.target.value)}
            />
            <span className={styles.configHelp}>{t("netquality.seconds")}</span>
            <button
              type="button"
              className={styles.smallButton}
              disabled={saving || Number(interval) === config.interval_seconds}
              onClick={() => save({ interval_seconds: Number(interval) })}
            >
              {t("netquality.save")}
            </button>
          </div>
          <div className={styles.configRow}>
            <strong>{t("netquality.lteSpeed")}</strong>
            <select
              className={styles.smallInput}
              style={{ width: "auto" }}
              value={config.lte_speed_test || "in_use"}
              disabled={saving}
              onChange={(e) => save({ lte_speed_test: e.target.value })}
            >
              <option value="in_use">{t("netquality.lteSpeedInUse")}</option>
              <option value="always">{t("netquality.lteSpeedAlways")}</option>
              <option value="never">{t("netquality.lteSpeedNever")}</option>
            </select>
            <span className={styles.configHelp}>{t("netquality.lteSpeedHelp")}</span>
          </div>
          <div className={styles.configRow}>
            <strong>{t("netquality.survey")}</strong>
            <span className={styles.configHelp}>{t("netquality.surveyHelp")}</span>
            {config.survey_active ? (
              <>
                <span className={styles.surveyActive}>{t("netquality.surveyActive", { until: formatTimestamp(config.survey_until) })}</span>
                <button type="button" className={styles.smallButton} disabled={saving} onClick={() => save({ survey_minutes: 0 })}>
                  {t("netquality.surveyStop")}
                </button>
              </>
            ) : (
              <button type="button" className={styles.smallButton} disabled={saving} onClick={() => save({ survey_minutes: SURVEY_MINUTES })}>
                {t("netquality.surveyStart")}
              </button>
            )}
          </div>
        </div>
      )}

      <div className={styles.qualityBlock}>
        <div className={styles.qualityLabel}>{t("netquality.latest")}</div>
        {latestByIface.length === 0 ? (
          <div className={styles.statusEmpty}>{t("netquality.noSamples")}</div>
        ) : (
          <ProbeResult samples={latestByIface} showTime compact />
        )}
      </div>
    </div>
  );
}
